// HotelCard — reusable hotel listing card for HotelsPage grid.
// All CTAs → notYetWiredToast (hotels BE doesn't exist yet).
// TODO(hotels-API-PR): Wire navigate + Reserve to real booking flow.

import { useState } from 'react';
import { Heart } from 'lucide-react';
import { notYetWiredToast } from './primitives';
import type { HotelMock } from '@/lib/hotels-mock';

const T = {
  bg: '#FFF5EE', surface: '#FFFFFF', ink: '#1A0F08', inkDim: '#7A6657',
  primary: '#FF5A3C', soft: '#FFE3D2', chipInk: '#B5391C',
  line: 'rgba(26,15,8,0.08)', accent: '#FFB800',
  sans: '"Inter Variable","Inter",system-ui,sans-serif',
  display: '"Fraunces Variable","Fraunces",Georgia,serif',
};

function tagColor(tag: string): { bg: string; color: string } {
  if (tag === 'EDITORS PICK') return { bg: T.accent, color: T.ink };
  if (tag === 'LUXE') return { bg: T.ink, color: T.bg };
  if (tag === 'GUEST FAV') return { bg: '#1F8A5B', color: '#fff' };
  return { bg: T.surface, color: T.ink };
}

interface HotelCardProps {
  hotel: HotelMock;
  onClick: () => void;
}

export function HotelCard({ hotel: h, onClick }: HotelCardProps) {
  const [imgIdx, setImgIdx] = useState(0);
  const [saved, setSaved] = useState(false);
  const tc = tagColor(h.tag);

  return (
    <div
      onClick={onClick}
      style={{
        background: T.surface, borderRadius: 26, overflow: 'hidden',
        border: `1px solid ${T.line}`, cursor: 'pointer',
        transition: 'box-shadow 200ms ease, transform 200ms ease',
        boxShadow: '0 8px 24px -8px rgba(0,0,0,0.08)',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 12px 24px -8px rgba(0,0,0,0.14)';
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 24px -8px rgba(0,0,0,0.08)';
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
      }}
    >
      {/* Photo + image-dot controls */}
      <div style={{ position: 'relative', paddingTop: '75%' /* 4:3 */ }}>
        <img
          src={h.imgs[imgIdx] ?? h.img}
          alt={h.name}
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%', objectFit: 'cover',
            transition: 'transform 600ms ease',
          }}
          loading="lazy"
        />

        {/* tag */}
        <div style={{
          position: 'absolute', top: 12, left: 12,
          padding: '4px 10px', borderRadius: 999,
          background: tc.bg, color: tc.color,
          fontFamily: T.sans, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
        }}>✦ {h.tag}</div>

        {/* save */}
        <button
          onClick={e => { e.stopPropagation(); setSaved(s => !s); notYetWiredToast('Save hotel'); }}
          style={{
            position: 'absolute', top: 12, right: 12,
            width: 36, height: 36, borderRadius: '50%',
            background: 'rgba(255,255,255,0.85)', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <Heart size={16} stroke={saved ? T.primary : '#000'} fill={saved ? T.primary : 'none'}/>
        </button>

        {/* image dots */}
        {h.imgs.length > 1 && (
          <div style={{
            position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', gap: 4,
          }}>
            {h.imgs.map((_, i) => (
              <div
                key={i}
                onClick={e => { e.stopPropagation(); setImgIdx(i); }}
                style={{
                  width: i === imgIdx ? 18 : 6, height: 6, borderRadius: 3,
                  background: i === imgIdx ? '#fff' : 'rgba(255,255,255,0.55)',
                  transition: 'all 250ms ease', cursor: 'pointer',
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: 18 }}>
        {/* name row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: T.sans, fontSize: 11, color: T.inkDim,
              letterSpacing: 1, fontWeight: 500,
            }}>{h.area.toUpperCase()} · {h.distance.toUpperCase()}</div>
            <div style={{
              fontFamily: T.display, fontSize: 20, fontWeight: 500,
              color: T.ink, marginTop: 3, lineHeight: 1.1, letterSpacing: -0.01,
            }}>{h.name}</div>
          </div>
          <div style={{
            padding: '4px 8px', borderRadius: 10, background: T.ink, color: '#fff',
            textAlign: 'center', minWidth: 42, flexShrink: 0,
          }}>
            <div style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{h.rating}</div>
            <div style={{ fontFamily: T.sans, fontSize: 9, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>{h.reviews.toLocaleString('en-IN')}</div>
          </div>
        </div>

        {/* perks */}
        <div style={{ display: 'flex', gap: 4, marginTop: 10, flexWrap: 'wrap' }}>
          {h.perks.map(p => (
            <span key={p} style={{
              padding: '3px 8px', borderRadius: 999,
              background: T.bg, color: T.inkDim,
              fontFamily: T.sans, fontSize: 10.5, fontWeight: 500,
              border: `1px solid ${T.line}`,
            }}>{p}</span>
          ))}
        </div>

        {/* price row */}
        <div style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          marginTop: 14, paddingTop: 14,
          borderTop: `1px dashed ${T.line}`,
        }}>
          <div>
            <div style={{ fontFamily: T.sans, fontSize: 11, color: '#1F8A5B', fontWeight: 600 }}>
              {h.freeCancellation ? 'FREE CANCELLATION' : h.ratingLabel.toUpperCase()}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: T.sans, fontSize: 11, color: T.inkDim, textDecoration: 'line-through' }}>
              ₹{(h.oldPrice * 83).toLocaleString('en-IN')}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
              <span style={{ fontFamily: T.display, fontSize: 22, fontWeight: 600, color: T.ink, letterSpacing: -0.01 }}>
                ₹{(h.price * 83).toLocaleString('en-IN')}
              </span>
              <span style={{ fontFamily: T.sans, fontSize: 11, color: T.inkDim }}>/night</span>
            </div>
          </div>
        </div>

        {/* Reserve CTA */}
        <button
          onClick={e => { e.stopPropagation(); notYetWiredToast('Hotels'); }}
          style={{
            marginTop: 14, width: '100%', height: 44, borderRadius: 999,
            background: T.primary, color: '#fff', border: 'none',
            fontFamily: T.sans, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            transition: 'background 200ms ease',
            boxShadow: '0 6px 16px rgba(255,90,60,0.20)',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#E84B30'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = T.primary; }}
        >
          reserve
        </button>
      </div>
    </div>
  );
}
